import { BaseService } from './core/BaseService';
import { apiClient } from './core/ApiClient';

class UserService extends BaseService {
  constructor() {
    super('/users');
  }

  // Get all users with enhanced filtering
  async getUsers(params = {}) {
    // Normalize role to lowercase if provided
    if (params.role) {
      params.role = params.role.toLowerCase();
    }
    return this.getPaginated(params);
  }

  // Get current user profile
  async getCurrentUser() {
    return apiClient.get('/users/me');
  }

  // Get single user by ID
  async getUser(userId) {
    return this.getById(userId);
  }

  // Create new user (Admin only)
  async createUser(userData) {
    this.validateUserData(userData);
    return this.create(userData);
  }

  // Update user
  async updateUser(userId, userData) {
    return this.update(userId, userData);
  }

  // Delete user (Admin only)
  async deleteUser(userId) {
    return this.delete(userId);
  }

  // Deactivate user (Admin only)
  async deactivateUser(userId) {
    return this.action(userId, 'deactivate');
  }

  // Activate user (Admin only)
  async activateUser(userId) {
    return this.action(userId, 'activate');
  }

  // Get users by branch
  async getUsersByBranch(branchId, params = {}) {
    if (!branchId) throw new Error('Branch ID is required');
    return apiClient.get(`/users/branch/${branchId}/users?${apiClient.buildQuery(params)}`);
  }

  // Get users by role
  async getUsersByRole(role, params = {}) {
    if (!role) throw new Error('Role is required');
    const normalizedRole = role.toLowerCase();
    return apiClient.get(`/users/role/${normalizedRole}/users?${apiClient.buildQuery(params)}`);
  }

  // Update user status
  async updateUserStatus(userId, isActive) {
    if (!userId) throw new Error('User ID is required');
    return this.patch(userId, { is_active: isActive });
  }

  // Get user permissions
  async getUserPermissions(userId) {
    return this.getRelated(userId, 'permissions');
  }

  // Update user permissions
  async updateUserPermissions(userId, permissions) {
    if (!userId) throw new Error('User ID is required');
    if (!Array.isArray(permissions)) throw new Error('Permissions must be an array');
    
    return apiClient.put(`${this.baseEndpoint}/${userId}/permissions`, { permissions });
  }

  // Get user statistics
  async getUserStats(userId) {
    return this.getRelated(userId, 'stats');
  }

  // Reset user password
  async resetUserPassword(userId) {
    return this.action(userId, 'reset-password');
  }

  // Bulk update users
  async bulkUpdateUsers(userIds, updateData) {
    return this.bulkUpdate(userIds, updateData);
  }

  // Search users
  async searchUsers(query, filters = {}) {
    return this.search(query, filters);
  }

  // Get user activity log
  async getUserActivity(userId, params = {}) {
    return this.getRelated(userId, 'activity', params);
  }

  // Get user tickets
  async getUserTickets(userId, params = {}) {
    return this.getRelated(userId, 'tickets', params);
  }

  // Change user password (Admin only)
  async changeUserPassword(userId, newPassword) {
    if (!userId) throw new Error('User ID is required');
    if (!newPassword) throw new Error('New password is required');
    
    return apiClient.post(`${this.baseEndpoint}/${userId}/change-password`, {
      new_password: newPassword
    });
  }

  // Send password reset email
  async sendPasswordResetEmail(userId) {
    return this.action(userId, 'send-password-reset');
  }

  // Get user roles (metadata)
  async getUserRoles() {
    return this.customEndpoint('roles', 'GET');
  }

  // Get user departments
  async getUserDepartments() {
    return this.customEndpoint('departments', 'GET');
  }

  // Bulk operations
  async bulkActivateUsers(userIds) {
    return this.customEndpoint('bulk-activate', 'POST', { user_ids: userIds });
  }

  async bulkDeactivateUsers(userIds) {
    return this.customEndpoint('bulk-deactivate', 'POST', { user_ids: userIds });
  }

  async bulkDeleteUsers(userIds) {
    return this.customEndpoint('bulk-delete', 'POST', { user_ids: userIds });
  }

  async bulkAssignRole(userIds, role) {
    if (!Array.isArray(userIds) || userIds.length === 0) {
      throw new Error('User IDs array is required');
    }
    if (!role) throw new Error('Role is required');
    
    return this.customEndpoint('bulk-assign-role', 'POST', {
      user_ids: userIds,
      role: role.toLowerCase()
    });
  }

  async bulkAssignBranch(userIds, branchId) {
    if (!Array.isArray(userIds) || userIds.length === 0) {
      throw new Error('User IDs array is required');
    }
    if (!branchId) throw new Error('Branch ID is required');
    
    return this.customEndpoint('bulk-assign-branch', 'POST', {
      user_ids: userIds,
      branch_id: branchId
    });
  }

  // Import/Export operations
  async importUsers(fileData, options = {}) {
    const formData = new FormData();
    formData.append('file', fileData);
    
    Object.entries(options).forEach(([key, value]) => {
      formData.append(key, value);
    });
    
    return apiClient.upload(`${this.baseEndpoint}/import`, formData);
  }

  async exportUsers(params = {}, format = 'csv') {
    return this.export(params, format);
  }

  // User validation
  validateUserData(userData) {
    const required = ['username', 'email', 'first_name', 'last_name', 'role'];
    const missing = required.filter(field => !userData[field]);
    
    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(', ')}`);
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email)) {
      throw new Error('Invalid email format');
    }
    
    // Validate role
    const validRoles = ['admin', 'user', 'customer', 'supplier'];
    if (!validRoles.includes(userData.role.toLowerCase())) {
      throw new Error('Invalid role. Must be one of: ' + validRoles.join(', '));
    }
    
    // Validate phone format if provided
    if (userData.phone && !/^\+?[1-9]\d{1,14}$/.test(userData.phone.replace(/[\s-]/g, ''))) {
      throw new Error('Invalid phone number format');
    }
  }

  // Check username availability
  async checkUsernameAvailability(username) {
    if (!username) throw new Error('Username is required');
    
    return this.customEndpoint('check-username', 'POST', { username });
  }

  // Check email availability
  async checkEmailAvailability(email) {
    if (!email) throw new Error('Email is required');
    
    return this.customEndpoint('check-email', 'POST', { email });
  }

  // User preferences
  async getUserPreferences(userId) {
    return this.getRelated(userId, 'preferences');
  }

  async updateUserPreferences(userId, preferences) {
    if (!userId) throw new Error('User ID is required');
    
    return apiClient.put(`${this.baseEndpoint}/${userId}/preferences`, preferences);
  }

  // User notifications settings
  async getUserNotificationSettings(userId) {
    return this.getRelated(userId, 'notification-settings');
  }

  async updateUserNotificationSettings(userId, settings) {
    if (!userId) throw new Error('User ID is required');
    
    return apiClient.put(`${this.baseEndpoint}/${userId}/notification-settings`, settings);
  }

  // User session management
  async getUserSessions(userId) {
    return this.getRelated(userId, 'sessions');
  }

  async terminateUserSession(userId, sessionId) {
    if (!userId || !sessionId) {
      throw new Error('User ID and session ID are required');
    }
    
    return apiClient.delete(`${this.baseEndpoint}/${userId}/sessions/${sessionId}`);
  }

  async terminateAllUserSessions(userId) {
    return this.action(userId, 'terminate-all-sessions');
  }
}

// Export singleton instance
export const userService = new UserService();

// Export class for testing or custom instances
export { UserService };